import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@utils/cn';

/**
 * Spinner variants using class-variance-authority
 */
const spinnerVariants = cva('animate-spin', {
  variants: {
    size: {
      xs: 'h-3 w-3',
      sm: 'h-4 w-4',
      md: 'h-6 w-6',
      lg: 'h-8 w-8',
      xl: 'h-12 w-12',
    },
    variant: {
      default: 'text-primary',
      muted: 'text-muted-foreground',
      light: 'text-white',
      dark: 'text-foreground',
    },
  },
  defaultVariants: {
    size: 'md',
    variant: 'default',
  },
});

export interface SpinnerProps
  extends Omit<React.SVGAttributes<SVGElement>, 'size'>,
    VariantProps<typeof spinnerVariants> {
  /**
   * Label for screen readers
   */
  label?: string;
}

/**
 * Spinner Component
 *
 * A loading indicator with multiple sizes and color variants.
 * Fully accessible with proper ARIA attributes.
 *
 * @example
 * // Basic spinner
 * <Spinner />
 *
 * @example
 * // Different sizes
 * <Spinner size="sm" />
 * <Spinner size="lg" />
 *
 * @example
 * // Different variants
 * <Spinner variant="muted" />
 * <Spinner variant="light" />
 *
 * @example
 * // With custom label
 * <Spinner label="Loading patient data..." />
 */
const Spinner = React.forwardRef<SVGSVGElement, SpinnerProps>(
  ({ className, size, variant, label = 'Loading...', ...props }, ref) => {
    return (
      <>
        <Loader2
          ref={ref}
          className={cn(spinnerVariants({ size, variant }), className)}
          aria-hidden="true"
          {...props}
        />
        <span className="sr-only">{label}</span>
      </>
    );
  },
);
Spinner.displayName = 'Spinner';

/**
 * Spinner Overlay Component
 *
 * A full-screen loading overlay with spinner
 */
export interface SpinnerOverlayProps extends SpinnerProps {
  /**
   * Show the overlay
   */
  show?: boolean;
  /**
   * Message to display below the spinner
   */
  message?: string;
  /**
   * Overlay opacity (0-100)
   */
  opacity?: number;
}

/**
 * SpinnerOverlay Component
 *
 * A full-screen loading overlay with spinner and optional message.
 *
 * @example
 * <SpinnerOverlay show={isLoading} message="Loading patient records..." />
 *
 * @example
 * <SpinnerOverlay
 *   show={isProcessing}
 *   message="Processing data..."
 *   size="lg"
 *   opacity={90}
 * />
 */
const SpinnerOverlay: React.FC<SpinnerOverlayProps> = ({
  show = true,
  message,
  size = 'lg',
  variant = 'default',
  opacity = 80,
  label,
}) => {
  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{
        backgroundColor: `rgba(255, 255, 255, ${opacity / 100})`,
      }}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex flex-col items-center gap-4">
        <Spinner size={size} variant={variant} label={label} />
        {message && (
          <p className="text-sm text-muted-foreground">{message}</p>
        )}
      </div>
    </div>
  );
};
SpinnerOverlay.displayName = 'SpinnerOverlay';

/**
 * Inline Spinner Component
 *
 * A spinner for inline use with text
 */
export interface InlineSpinnerProps extends SpinnerProps {
  text?: string;
}

/**
 * InlineSpinner Component
 *
 * A spinner designed for inline use with text.
 *
 * @example
 * <InlineSpinner text="Loading..." />
 * <InlineSpinner size="sm" text="Saving changes..." />
 */
const InlineSpinner: React.FC<InlineSpinnerProps> = ({
  text,
  size = 'sm',
  ...props
}) => {
  return (
    <span className="inline-flex items-center gap-2">
      <Spinner size={size} {...props} />
      {text && <span className="text-sm text-muted-foreground">{text}</span>}
    </span>
  );
};
InlineSpinner.displayName = 'InlineSpinner';

/**
 * CSS-based Spinner (alternative to icon-based spinner)
 */
export interface CssSpinnerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof spinnerVariants> {}

/**
 * CssSpinner Component
 *
 * A pure CSS spinner (no icon dependencies).
 *
 * @example
 * <CssSpinner />
 * <CssSpinner size="lg" variant="default" />
 */
const CssSpinner = React.forwardRef<HTMLDivElement, CssSpinnerProps>(
  ({ className, size, variant, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          spinnerVariants({ size, variant }),
          'rounded-full border-2 border-current border-t-transparent',
          className,
        )}
        role="status"
        aria-label="Loading"
        {...props}
      >
        <span className="sr-only">Loading...</span>
      </div>
    );
  },
);
CssSpinner.displayName = 'CssSpinner';

export { Spinner, SpinnerOverlay, InlineSpinner, CssSpinner, spinnerVariants };
